// src/pages/production/DashboardTab.tsx
//
// Phase 2A overview. Stat cards across the top, recipe cost overview table
// underneath. Live-derived from current data — no caching.

import { useEffect, useMemo, useState } from 'react';
import { Package, ChefHat, Boxes, TrendingUp } from 'lucide-react';
import {
  getItems, getRecipes, getProducts, calcRecipeCost, calcProductCost,
} from '../../services/productionStorage';
import type { ProductionItem, ProductionRecipe, ProductionProduct } from '../../types/production';

interface DashboardTabProps {
  slug: string;
}

export default function DashboardTab({ slug }: DashboardTabProps) {
  const [items, setItems] = useState<ProductionItem[]>([]);
  const [recipes, setRecipes] = useState<ProductionRecipe[]>([]);
  const [products, setProducts] = useState<ProductionProduct[]>([]);

  useEffect(() => {
    setItems(getItems(slug));
    setRecipes(getRecipes(slug));
    setProducts(getProducts(slug));
  }, [slug]);

  const itemsById = useMemo(() => {
    const m: Record<string, ProductionItem> = {};
    for (const i of items) m[i.id] = i;
    return m;
  }, [items]);

  const recipesById = useMemo(() => {
    const m: Record<string, ProductionRecipe> = {};
    for (const r of recipes) m[r.id] = r;
    return m;
  }, [recipes]);

  const recipeSummaries = useMemo(() => {
    return recipes.map((r) => {
      const { costPerUnit } = calcRecipeCost(r, itemsById);
      const margin = r.sellPrice && r.sellPrice > 0
        ? ((r.sellPrice - costPerUnit) / r.sellPrice) * 100
        : null;
      return { recipe: r, costPerUnit, margin };
    });
  }, [recipes, itemsById]);

  const productSummaries = useMemo(() => {
    return products.map((p) => {
      const { totalCost } = calcProductCost(p, recipesById, itemsById);
      const margin = p.sellPrice && p.sellPrice > 0
        ? ((p.sellPrice - totalCost) / p.sellPrice) * 100
        : null;
      return { product: p, totalCost, margin };
    });
  }, [products, recipesById, itemsById]);

  const avgRecipeMargin = useMemo(() => {
    const withMargin = recipeSummaries.filter((r) => r.margin != null);
    if (withMargin.length === 0) return null;
    return withMargin.reduce((s, r) => s + r.margin!, 0) / withMargin.length;
  }, [recipeSummaries]);

  const totalInventoryValue = useMemo(() => {
    return items.reduce((s, i) => s + (i.onHand ?? 0) * i.costPerUnit, 0);
  }, [items]);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Dashboard</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Overview of items, recipes, products, and margins for the active tenant.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Items" value={items.length} icon={Package} accent="orange" />
        <StatCard label="Recipes" value={recipes.length} icon={ChefHat} accent="orange" />
        <StatCard label="Products" value={products.length} icon={Boxes} accent="orange" />
        <StatCard
          label="Avg recipe margin"
          value={avgRecipeMargin != null ? `${avgRecipeMargin.toFixed(1)}%` : '—'}
          icon={TrendingUp}
          accent={
            avgRecipeMargin == null ? 'gray' :
            avgRecipeMargin > 50 ? 'emerald' :
            avgRecipeMargin > 30 ? 'orange' : 'red'
          }
        />
      </div>

      {totalInventoryValue > 0 && (
        <div className="mb-6 p-3 rounded-lg bg-slate-800/30 border border-slate-700/40 text-sm">
          <span className="text-slate-400">On-hand inventory value: </span>
          <span className="text-white font-mono font-medium">${totalInventoryValue.toFixed(2)}</span>
          <span className="text-xs text-slate-500 ml-2">(sum of items' onHand × costPerUnit)</span>
        </div>
      )}

      {/* Recipe cost overview */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-slate-300 mb-2">Recipe cost overview</h3>
        {recipeSummaries.length === 0 ? (
          <div className="p-6 rounded-2xl bg-slate-800/30 border border-slate-700/40 text-center text-sm text-slate-500">
            No recipes yet. Add some on the Recipes tab.
          </div>
        ) : (
          <div className="rounded-2xl bg-slate-800/30 border border-slate-700/40 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-slate-900/40">
                  <th className="px-4 py-3 font-medium">Recipe</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium text-right">Cost / unit</th>
                  <th className="px-4 py-3 font-medium text-right">Sell</th>
                  <th className="px-4 py-3 font-medium text-right">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/40">
                {recipeSummaries.map(({ recipe, costPerUnit, margin }) => (
                  <tr key={recipe.id} className="hover:bg-slate-700/20">
                    <td className="px-4 py-3 text-white font-medium">{recipe.name}</td>
                    <td className="px-4 py-3 text-slate-300">{recipe.category || '—'}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-200">${costPerUnit.toFixed(4)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-300">
                      {recipe.sellPrice != null ? `$${recipe.sellPrice.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {margin == null ? (
                        <span className="text-slate-500">—</span>
                      ) : (
                        <span className={`font-mono ${
                          margin > 50 ? 'text-emerald-400' : margin > 30 ? 'text-orange-400' : 'text-red-400'
                        }`}>
                          {margin.toFixed(1)}%
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Product margins */}
      {productSummaries.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-slate-300 mb-2">Product margins</h3>
          <div className="rounded-2xl bg-slate-800/30 border border-slate-700/40 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-slate-900/40">
                  <th className="px-4 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">SKU</th>
                  <th className="px-4 py-3 font-medium text-right">Total cost</th>
                  <th className="px-4 py-3 font-medium text-right">Sell</th>
                  <th className="px-4 py-3 font-medium text-right">Margin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/40">
                {productSummaries.map(({ product, totalCost, margin }) => (
                  <tr key={product.id} className="hover:bg-slate-700/20">
                    <td className="px-4 py-3 text-white font-medium">{product.name}</td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{product.sku || '—'}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-200">${totalCost.toFixed(4)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-300">
                      {product.sellPrice != null ? `$${product.sellPrice.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {margin == null ? (
                        <span className="text-slate-500">—</span>
                      ) : (
                        <span className={`font-mono ${
                          margin > 50 ? 'text-emerald-400' : margin > 30 ? 'text-orange-400' : 'text-red-400'
                        }`}>
                          {margin.toFixed(1)}%
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label, value, icon: Icon, accent,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ size?: number }>;
  accent: 'orange' | 'emerald' | 'red' | 'gray';
}) {
  const accentClass = {
    orange:  'text-orange-400 bg-orange-500/10',
    emerald: 'text-emerald-400 bg-emerald-500/10',
    red:     'text-red-400 bg-red-500/10',
    gray:    'text-slate-400 bg-slate-500/10',
  }[accent];
  return (
    <div className="p-4 rounded-2xl bg-slate-800/30 border border-slate-700/40">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-400 uppercase tracking-wider">{label}</span>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${accentClass}`}>
          <Icon size={14} />
        </div>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}
